import { config } from '../config';
import { trackInterstitialAdViewed } from '../analytics';
import { logInterstitialStage } from './interstitialLogger';
import { canRequestGoogleAds, canShowGoogleInterstitial } from '../utils/googleCmp';

const GPT_SCRIPT_SOURCE = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
const GPT_SCRIPT_ID = 'gdp-gpt-script';
const GPT_BOOTSTRAP_STATE_KEY = '__GDP_GPT_BOOTSTRAP__';
const H5_INTERSTITIAL_TIMEOUT_MS = 1500;

const noop = () => {};
const normalizePathSegment = (value) => String(value || '').trim().replace(/^\/+|\/+$/g, '');
const getWindow = () => (typeof window === 'undefined' ? null : window);

const createGptInterstitialMemoryState = () => ({
  status: 'idle',
  hasAdInMemory: false,
  lastRenderedAt: null,
  lastDisplayedAt: null,
  lastDisplaySource: null,
  placementInfo: null,
});

const getGptBootstrapState = () => {
  const win = getWindow();
  if (!win) {
    return null;
  }

  if (!win[GPT_BOOTSTRAP_STATE_KEY] || typeof win[GPT_BOOTSTRAP_STATE_KEY] !== 'object') {
    win[GPT_BOOTSTRAP_STATE_KEY] = {
      scriptStatus: 'idle',
      scriptPromise: null,
      slot: null,
      slotDefined: false,
      servicesEnabled: false,
      slotRegistered: false,
      pubadsListenersAttached: false,
      pubadsDebugContext: null,
      lastPrefetchPlacementInfo: null,
      interstitialMemoryState: createGptInterstitialMemoryState(),
    };
  }

  if (
    !win[GPT_BOOTSTRAP_STATE_KEY].interstitialMemoryState
    || typeof win[GPT_BOOTSTRAP_STATE_KEY].interstitialMemoryState !== 'object'
  ) {
    win[GPT_BOOTSTRAP_STATE_KEY].interstitialMemoryState = createGptInterstitialMemoryState();
  }

  return win[GPT_BOOTSTRAP_STATE_KEY];
};

const getGptInterstitialMemoryState = (state) => {
  if (!state) {
    return createGptInterstitialMemoryState();
  }

  if (!state.interstitialMemoryState || typeof state.interstitialMemoryState !== 'object') {
    state.interstitialMemoryState = createGptInterstitialMemoryState();
  }

  return state.interstitialMemoryState;
};

const getGptInterstitialMemoryDebugDetails = (memoryState, now = Date.now()) => ({
  interstitialMemoryStatus: memoryState?.status || 'idle',
  hasAdInMemory: memoryState?.hasAdInMemory === true,
  lastRenderedAt: memoryState?.lastRenderedAt || null,
  lastDisplayedAt: memoryState?.lastDisplayedAt || null,
  lastDisplaySource: memoryState?.lastDisplaySource || null,
  msSinceLastRendered: Number.isFinite(memoryState?.lastRenderedAt)
    ? now - memoryState.lastRenderedAt
    : null,
  placementInfo: memoryState?.placementInfo || null,
});

const ensureGoogletag = () => {
  const win = getWindow();
  if (!win) {
    return null;
  }

  win.googletag = win.googletag || { cmd: [] };
  if (typeof win.googletag.cmd?.push !== 'function') {
    win.googletag.cmd = [];
  }
  return win.googletag;
};

const finishWithResult = ({ resolve, cleanup, result }) => {
  cleanup();
  resolve(result);
};

const writeInterstitialConsoleLog = (_level, stage, details = {}) => {
  logInterstitialStage(stage, details);
};

const getSlotDebugDetails = (slot) => {
  if (!slot) {
    return {};
  }

  return {
    adUnitPath: typeof slot.getAdUnitPath === 'function' ? slot.getAdUnitPath() : null,
    slotElementId: typeof slot.getSlotElementId === 'function' ? slot.getSlotElementId() : null,
  };
};

const getSlotEventDebugDetails = (event) => {
  const slot = event?.slot || null;
  return {
    ...getSlotDebugDetails(slot),
    serviceName: event?.serviceName || null,
    responseIdentifier: event?.responseIdentifier || null,
    size: event?.size || null,
    isEmpty: event?.isEmpty === true,
    inViewPercentage: typeof event?.inViewPercentage === 'number' ? event.inViewPercentage : null,
    creativeId: event?.creativeId ?? null,
    lineItemId: event?.lineItemId ?? null,
    sourceAgnosticCreativeId: event?.sourceAgnosticCreativeId ?? null,
    sourceAgnosticLineItemId: event?.sourceAgnosticLineItemId ?? null,
  };
};

const buildGptInterstitialAdUnitPath = ({
  networkCode = config.GPT_NETWORK_CODE,
  adUnitCode = config.GPT_INTERSTITIAL_AD_UNIT_CODE,
} = {}) => {
  const normalizedNetworkCode = normalizePathSegment(networkCode);
  const normalizedAdUnitCode = normalizePathSegment(adUnitCode);

  if (!normalizedNetworkCode || !normalizedAdUnitCode) {
    return null;
  }

  return `/${normalizedNetworkCode}/${normalizedAdUnitCode}`;
};

const getGptAdUnitPath = () => buildGptInterstitialAdUnitPath();

export class NoopAdProvider {
  async initialize() {}

  async showInterstitial({ requestDetails, onEvent = noop }) {
    onEvent('info', 'skipped', {
      ...requestDetails,
      reason: 'ads_disabled',
    });
    return {
      shown: false,
      reason: 'ads_disabled',
    };
  }

  async showRewarded({ requestDetails, onEvent = noop }) {
    onEvent('info', 'skipped', {
      ...requestDetails,
      reason: 'ads_disabled',
    });
    return {
      shown: false,
      reason: 'ads_disabled',
    };
  }
}

export class VignetteAdProvider {
  async initialize() {}

  async showInterstitial({ requestDetails, onEvent = noop }) {
    onEvent('info', 'anchor_trigger_delegated', {
      ...requestDetails,
      providerType: 'VIGNETTE',
    });
    return {
      shown: false,
      reason: 'anchor_managed',
    };
  }

  async showRewarded({ requestDetails, onEvent = noop }) {
    onEvent('warn', 'skipped', {
      ...requestDetails,
      providerType: 'VIGNETTE',
      reason: 'rewarded_not_supported',
    });
    return {
      shown: false,
      reason: 'rewarded_not_supported',
    };
  }
}

export class H5AdProvider {
  constructor({ getAdsScriptStatus, isProductionEnvironment }) {
    this.getAdsScriptStatus = getAdsScriptStatus;
    this.isProductionEnvironment = isProductionEnvironment;
  }

  async initialize() {}

  async showInterstitial({ requestDetails, onEvent = noop }) {
    if (!canShowGoogleInterstitial()) return { shown: false, reason: 'consent_pending' };
    const scriptStatus = this.getAdsScriptStatus();

    if (typeof window === 'undefined' || typeof window.adBreak !== 'function') {
      onEvent('warn', 'skipped', {
        ...requestDetails,
        scriptStatus,
        reason: 'api_unavailable',
      });
      return { shown: false, reason: 'api_unavailable' };
    }

    if (scriptStatus === 'blocked') {
      onEvent('warn', 'skipped', {
        ...requestDetails,
        scriptStatus,
        reason: 'script_blocked',
      });
      return { shown: false, reason: 'script_blocked' };
    }

    onEvent('info', 'requested', {
      ...requestDetails,
      scriptStatus,
      testMode: !this.isProductionEnvironment,
    });

    return new Promise((resolve) => {
      let settled = false;
      let adWasShown = false;
      let viewTracked = false;
      const startedAt = Date.now();

      const cleanup = () => {
        window.clearTimeout(timeoutId);
      };

      const finish = (result) => {
        if (settled) {
          return;
        }
        settled = true;
        finishWithResult({ resolve, cleanup, result });
      };

      const timeoutId = window.setTimeout(() => {
        if (settled) {
          return;
        }

        const timeoutScriptStatus = this.getAdsScriptStatus();
        onEvent('warn', 'timeout', {
          ...requestDetails,
          elapsedMs: Date.now() - startedAt,
          scriptStatus: timeoutScriptStatus,
          adBreakStubActive: window.adBreak?.__GDP_STUB__ === true,
        });
        finish({
          shown: false,
          reason: timeoutScriptStatus === 'blocked' ? 'script_blocked' : 'timeout',
        });
      }, H5_INTERSTITIAL_TIMEOUT_MS);

      try {
        window.adBreak({
          type: requestDetails.exitType === 'archive' ? 'browse' : 'next',
          name: requestDetails.placementName,
          beforeAd: () => {
            adWasShown = true;

            if (!viewTracked) {
              viewTracked = true;
              try {
                trackInterstitialAdViewed({
                  source_surface: 'ads_interstitial',
                  game_slug: requestDetails?.gameSlug || null,
                  game_date: requestDetails?.date || null,
                  placement_name: requestDetails?.placementName || null,
                  provider_type: 'H5',
                  exit_type: requestDetails?.exitType || null,
                  target_path: requestDetails?.targetPath || null,
                  source_stage: 'beforeAd',
                });
              } catch (_) {
                // Analytics should stay non-blocking.
              }
            }

            onEvent('debug', 'beforeAd', requestDetails);
          },
          afterAd: () => {
            onEvent('debug', 'afterAd', requestDetails);
          },
          adBreakDone: (placementInfo) => {
            onEvent('debug', 'adBreakDone', {
              ...requestDetails,
              shown: adWasShown,
              placementInfo: placementInfo || null,
            });

            if (!adWasShown) {
              onEvent('info', 'not_shown', {
                ...requestDetails,
                placementInfo: placementInfo || null,
              });
            }

            finish({
              shown: adWasShown,
              reason: adWasShown ? undefined : 'not_shown',
              placementInfo: placementInfo || null,
            });
          },
        });
      } catch (error) {
        onEvent('warn', 'request_failed', {
          ...requestDetails,
          error: error instanceof Error
            ? {
                name: error.name,
                message: error.message,
              }
            : String(error),
        });
        finish({
          shown: false,
          reason: 'request_failed',
        });
      }
    });
  }

  async showRewarded({
    requestDetails,
    onEvent = noop,
    onBeforeReward = noop,
    onBeforeAd = noop,
    onAdViewed = noop,
    onAdDismissed = noop,
    onAfterAd = noop,
    onAdBreakDone = noop,
  }) {
    if (!canShowGoogleInterstitial()) return { shown: false, rewardViewed: false, reason: 'consent_pending' };
    const scriptStatus = this.getAdsScriptStatus();

    if (typeof window === 'undefined' || typeof window.adBreak !== 'function') {
      onEvent('warn', 'skipped', {
        ...requestDetails,
        scriptStatus,
        reason: 'api_unavailable',
      });
      return { shown: false, reason: 'api_unavailable' };
    }

    if (scriptStatus === 'blocked') {
      onEvent('warn', 'skipped', {
        ...requestDetails,
        scriptStatus,
        reason: 'script_blocked',
      });
      return { shown: false, reason: 'script_blocked' };
    }

    onEvent('info', 'requested', {
      ...requestDetails,
      scriptStatus,
      testMode: !this.isProductionEnvironment,
      adType: 'reward',
    });

    return new Promise((resolve) => {
      let settled = false;
      let adWasShown = false;
      let rewardViewed = false;
      const startedAt = Date.now();

      const cleanup = () => {
        window.clearTimeout(timeoutId);
      };

      const finish = (result) => {
        if (settled) {
          return;
        }
        settled = true;
        finishWithResult({ resolve, cleanup, result });
      };

      const timeoutId = window.setTimeout(() => {
        if (settled) {
          return;
        }

        const timeoutScriptStatus = this.getAdsScriptStatus();
        onEvent('warn', 'timeout', {
          ...requestDetails,
          elapsedMs: Date.now() - startedAt,
          scriptStatus: timeoutScriptStatus,
          adBreakStubActive: window.adBreak?.__GDP_STUB__ === true,
          adType: 'reward',
        });
        finish({
          shown: false,
          reason: timeoutScriptStatus === 'blocked' ? 'script_blocked' : 'timeout',
        });
      }, H5_INTERSTITIAL_TIMEOUT_MS);

      try {
        window.adBreak({
          type: 'reward',
          name: requestDetails.placementName,
          beforeReward: async (showAdFn) => {
            onEvent('debug', 'beforeReward', requestDetails);
            try {
              await onBeforeReward({
                requestDetails,
                showAdFn: () => {
                  if (canShowGoogleInterstitial()) showAdFn();
                  else finish({ shown: false, rewardViewed: false, reason: 'consent_pending' });
                },
              });
            } catch (error) {
              onEvent('warn', 'beforeReward_failed', {
                ...requestDetails,
                error: error instanceof Error
                  ? {
                      name: error.name,
                      message: error.message,
                    }
                  : String(error),
              });
              finish({
                shown: false,
                reason: 'before_reward_failed',
              });
            }
          },
          beforeAd: () => {
            adWasShown = true;
            onEvent('debug', 'beforeAd', requestDetails);
            onBeforeAd({ requestDetails });
          },
          adViewed: async () => {
            rewardViewed = true;
            onEvent('info', 'adViewed', requestDetails);
            await onAdViewed({ requestDetails });
          },
          adDismissed: () => {
            onEvent('info', 'adDismissed', requestDetails);
            onAdDismissed({ requestDetails });
          },
          afterAd: () => {
            onEvent('debug', 'afterAd', requestDetails);
            onAfterAd({ requestDetails });
          },
          adBreakDone: (placementInfo) => {
            onEvent('debug', 'adBreakDone', {
              ...requestDetails,
              shown: adWasShown,
              rewardViewed,
              placementInfo: placementInfo || null,
              adType: 'reward',
            });

            onAdBreakDone({
              requestDetails,
              shown: adWasShown,
              rewardViewed,
              placementInfo: placementInfo || null,
            });

            if (!adWasShown) {
              onEvent('info', 'not_shown', {
                ...requestDetails,
                placementInfo: placementInfo || null,
                adType: 'reward',
              });
            }

            finish({
              shown: adWasShown,
              rewardViewed,
              reason: adWasShown ? undefined : 'not_shown',
              placementInfo: placementInfo || null,
            });
          },
        });
      } catch (error) {
        onEvent('warn', 'request_failed', {
          ...requestDetails,
          error: error instanceof Error
            ? {
                name: error.name,
                message: error.message,
              }
            : String(error),
          adType: 'reward',
        });
        finish({
          shown: false,
          reason: 'request_failed',
        });
      }
    });
  }
}

export class GptAdProvider {
  handleSlotRenderEnded(onEvent, requestDetails, event) {
    const state = getGptBootstrapState();
    const memoryState = getGptInterstitialMemoryState(state);
    const now = Date.now();
    const nextStatus = event?.isEmpty === true ? 'empty' : 'loaded';
    const placementInfo = event?.isEmpty === true
      ? { isEmpty: true }
      : {
          isEmpty: false,
          responseIdentifier: event?.responseIdentifier || null,
        };

    memoryState.status = nextStatus;
    memoryState.hasAdInMemory = event?.isEmpty !== true;
    memoryState.lastRenderedAt = now;
    memoryState.placementInfo = placementInfo;

    if (event?.isEmpty === true) {
      memoryState.lastDisplayedAt = null;
      memoryState.lastDisplaySource = null;
    }

    if (state) {
      state.lastPrefetchPlacementInfo = placementInfo;
    }

    this.emitLifecycleEvent(onEvent, 'interstitial_state_updated', {
      ...(requestDetails || {}),
      transition: nextStatus === 'empty' ? 'idle->empty' : 'idle->loaded',
      ...getSlotEventDebugDetails(event),
      ...getGptInterstitialMemoryDebugDetails(memoryState, now),
    }, nextStatus === 'empty' ? 'info' : 'debug');
  }

  markInterstitialDisplayed(onEvent, requestDetails, sourceStage, event) {
    const state = getGptBootstrapState();
    const memoryState = getGptInterstitialMemoryState(state);
    const now = Date.now();

    if (!memoryState.hasAdInMemory) {
      this.emitLifecycleEvent(onEvent, 'display_signal_ignored', {
        ...(requestDetails || {}),
        sourceStage,
        ...getSlotEventDebugDetails(event),
        ...getGptInterstitialMemoryDebugDetails(memoryState, now),
      });
      return;
    }

    memoryState.status = 'shown';
    memoryState.hasAdInMemory = false;
    memoryState.lastDisplayedAt = now;
    memoryState.lastDisplaySource = sourceStage;

    try {
      trackInterstitialAdViewed({
        source_surface: 'ads_interstitial',
        game_slug: requestDetails?.gameSlug || null,
        game_date: requestDetails?.date || null,
        placement_name: requestDetails?.placementName || null,
        provider_type: 'GPT',
        exit_type: requestDetails?.exitType || null,
        target_path: requestDetails?.targetPath || null,
        source_stage: sourceStage,
        ad_unit_path: typeof event?.slot?.getAdUnitPath === 'function'
          ? event.slot.getAdUnitPath()
          : null,
        slot_element_id: typeof event?.slot?.getSlotElementId === 'function'
          ? event.slot.getSlotElementId()
          : null,
        response_identifier: event?.responseIdentifier || null,
        in_view_percentage: typeof event?.inViewPercentage === 'number'
          ? event.inViewPercentage
          : null,
        creative_id: event?.creativeId ?? null,
        line_item_id: event?.lineItemId ?? null,
        source_agnostic_creative_id: event?.sourceAgnosticCreativeId ?? null,
        source_agnostic_line_item_id: event?.sourceAgnosticLineItemId ?? null,
      });
    } catch (_) {
      // Analytics should stay non-blocking.
    }

    this.emitLifecycleEvent(onEvent, 'interstitial_state_updated', {
      ...(requestDetails || {}),
      transition: 'loaded->shown',
      sourceStage,
      ...getSlotEventDebugDetails(event),
      ...getGptInterstitialMemoryDebugDetails(memoryState, now),
    }, 'info');
  }

  emitLifecycleEvent(onEvent, stage, details = {}, level = 'debug') {
    onEvent(level, stage, {
      providerType: 'GPT',
      ...details,
    });
  }

  setPubadsDebugContext(requestDetails = null) {
    const state = getGptBootstrapState();
    if (!state) {
      return;
    }

    state.pubadsDebugContext = requestDetails
      ? {
          placementName: requestDetails.placementName || null,
          gameSlug: requestDetails.gameSlug || null,
          date: requestDetails.date || null,
          exitType: requestDetails.exitType || null,
          targetPath: requestDetails.targetPath || null,
        }
      : null;
  }

  attachPubadsDebugListeners(onEvent = noop) {
    const state = getGptBootstrapState();
    const interstitialSlot = state?.slot || null;

    if (!state || !interstitialSlot || !window.googletag?.pubads) {
      return;
    }

    if (state.pubadsListenersAttached) {
      return;
    }

    window.googletag.cmd.push(() => {
      const latestState = getGptBootstrapState();
      if (latestState?.pubadsListenersAttached) return;
      const slot = latestState?.slot || null;
      const pubads = window.googletag?.pubads?.();

      if (!slot || !pubads || typeof pubads.addEventListener !== 'function') {
        return;
      }

      const isCurrentSlot = (event) => !event?.slot || event.slot === getGptBootstrapState()?.slot;
      const emitPubadsEvent = (stage, event, extraDetails = {}) => {
        if (!isCurrentSlot(event)) {
          return;
        }

        const context = getGptBootstrapState()?.pubadsDebugContext || {};
        this.emitLifecycleEvent(onEvent, stage, {
          ...context,
          ...getSlotEventDebugDetails(event),
          ...extraDetails,
        });
      };

      pubads.addEventListener('slotRequested', (event) => {
        emitPubadsEvent('slotRequested', event);
      });

      pubads.addEventListener('slotResponseReceived', (event) => {
        emitPubadsEvent('slotResponseReceived', event);
      });

      pubads.addEventListener('slotRenderEnded', (event) => {
        if (!isCurrentSlot(event)) return;
        emitPubadsEvent('slotRenderEnded', event);
        this.handleSlotRenderEnded(onEvent, getGptBootstrapState()?.pubadsDebugContext || {}, event);
      });

      pubads.addEventListener('slotOnload', (event) => {
        emitPubadsEvent('slotOnload', event);
      });

      pubads.addEventListener('slotVisibilityChanged', (event) => {
        if (!isCurrentSlot(event)) return;
        emitPubadsEvent('slotVisibilityChanged', event);

        if (typeof event?.inViewPercentage === 'number' && event.inViewPercentage > 0) {
          this.markInterstitialDisplayed(onEvent, getGptBootstrapState()?.pubadsDebugContext || {}, 'slotVisibilityChanged', event);
        }
      });

      pubads.addEventListener('impressionViewable', (event) => {
        if (!isCurrentSlot(event)) return;
        emitPubadsEvent('impressionViewable', event);
        this.markInterstitialDisplayed(onEvent, getGptBootstrapState()?.pubadsDebugContext || {}, 'impressionViewable', event);
      });

      latestState.pubadsListenersAttached = true;
      this.emitLifecycleEvent(onEvent, 'pubads_debug_listeners_attached', {
        ...getSlotDebugDetails(slot),
      });
    });
  }

  async initialize() {
    if (!canShowGoogleInterstitial()) return;
    try {
      const state = getGptBootstrapState();
      this.emitLifecycleEvent((level, stage, details) => {
        writeInterstitialConsoleLog(level, stage, details);
      }, 'initialize_started', {
        scriptStatus: state?.scriptStatus || 'unknown',
        slotDefined: state?.slotDefined === true,
        adUnitPath: getGptAdUnitPath(),
      });
      await this.ensureSlot();
      this.attachPubadsDebugListeners((level, stage, details) => {
        writeInterstitialConsoleLog(level, stage, details);
      });
      await this.registerSlot();
    } catch (_) {
      // Initialization should remain non-blocking.
    }
  }

  async registerSlot(onEvent = noop) {
    const interstitialSlot = await this.ensureSlot();
    const state = getGptBootstrapState();

    if (!interstitialSlot || !state) {
      return null;
    }

    if (state.slotRegistered) {
      return interstitialSlot;
    }

    return new Promise((resolve, reject) => {
      try {
        window.googletag.cmd.push(() => {
          if (!canShowGoogleInterstitial()) {
            resolve(null);
            return;
          }
          if (state.slotRegistered) {
            resolve(interstitialSlot);
            return;
          }
          onEvent('debug', 'slot_registration_requested', {
            providerType: 'GPT',
          });
          window.googletag.display(interstitialSlot);
          state.slotRegistered = true;
          onEvent('debug', 'slot_registered', {
            providerType: 'GPT',
          });
          resolve(interstitialSlot);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  async loadScript() {
    if (!canRequestGoogleAds()) return null;
    const win = getWindow();
    const state = getGptBootstrapState();

    if (!win || !state) {
      throw new Error('window_unavailable');
    }

    if (state.scriptStatus === 'loaded') {
      return;
    }

    if (state.scriptStatus === 'blocked') {
      throw new Error('script_blocked');
    }

    if (state.scriptPromise) {
      return state.scriptPromise;
    }

    ensureGoogletag();
    state.scriptStatus = 'loading';

    state.scriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.getElementById(GPT_SCRIPT_ID);
      if (existingScript) {
        if (win.googletag?.apiReady === true || typeof win.googletag?.pubads === 'function') {
          state.scriptStatus = 'loaded';
          resolve();
          return;
        }

        existingScript.addEventListener('load', () => {
          state.scriptStatus = 'loaded';
          resolve();
        }, { once: true });
        existingScript.addEventListener('error', () => {
          state.scriptStatus = 'blocked';
          reject(new Error('script_blocked'));
        }, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = GPT_SCRIPT_ID;
      script.async = true;
      script.src = GPT_SCRIPT_SOURCE;
      script.crossOrigin = 'anonymous';

      script.addEventListener('load', () => {
        state.scriptStatus = 'loaded';
        resolve();
      }, { once: true });

      script.addEventListener('error', () => {
        state.scriptStatus = 'blocked';
        reject(new Error('script_blocked'));
      }, { once: true });

      document.head.appendChild(script);
    }).catch((error) => {
      state.scriptPromise = null;
      throw error;
    });

    return state.scriptPromise;
  }

  async ensureSlot() {
    const win = getWindow();
    const state = getGptBootstrapState();
    if (!win || !state) {
      return null;
    }

    await this.loadScript();
    if (!canShowGoogleInterstitial()) return null;

    if (state.slotDefined) {
      return state.slot;
    }

    ensureGoogletag();

    const adUnitPath = getGptAdUnitPath();
    if (!adUnitPath) {
      return null;
    }

    const slot = await new Promise((resolve) => {
      win.googletag.cmd.push(() => {
        if (!canShowGoogleInterstitial()) {
          resolve(null);
          return;
        }
        let nextSlot = state.slot;

        if (!nextSlot) {
          nextSlot = win.googletag.defineOutOfPageSlot(
            adUnitPath,
            win.googletag.enums.OutOfPageFormat.INTERSTITIAL,
          );

          if (nextSlot) {
            nextSlot.addService(win.googletag.pubads());
            if (typeof nextSlot.setConfig === 'function') {
              nextSlot.setConfig({
                interstitial: {
                  triggers: {
                    endOfArticle: false,
                    inactivity: false,
                    navBar: false,
                    unhideWindow: false,
                    continueReading: false,
                  },
                },
              });
            }
          }

          state.slot = nextSlot;
        }

        if (!state.servicesEnabled) {
          win.googletag.enableServices();
          state.servicesEnabled = true;
        }

        state.slotDefined = true;
        resolve(state.slot);
      });
    });

    return slot;
  }

  suspend() {
    const state = getGptBootstrapState();
    if (!state?.slot || !window.googletag?.cmd) return;
    window.googletag.cmd.push(() => {
      if (canShowGoogleInterstitial()) return;
      window.googletag.destroySlots?.([state.slot]);
      state.slot = null;
      state.slotDefined = false;
      state.slotRegistered = false;
      state.interstitialMemoryState = createGptInterstitialMemoryState();
    });
  }

  async showInterstitial({ requestDetails, onEvent = noop }) {
    if (!canShowGoogleInterstitial()) return { shown: false, reason: 'consent_pending' };
    this.setPubadsDebugContext(requestDetails);
    this.attachPubadsDebugListeners(onEvent);
    onEvent('info', 'anchor_trigger_delegated', requestDetails);
    return {
      shown: false,
      reason: 'anchor_managed',
    };
  }

  async showRewarded({ requestDetails, onEvent = noop }) {
    onEvent('warn', 'skipped', {
      ...requestDetails,
      providerType: 'GPT',
      reason: 'rewarded_not_supported',
    });
    return {
      shown: false,
      reason: 'rewarded_not_supported',
    };
  }
}

export const createInterstitialProvider = ({
  providerType,
  getAdsScriptStatus,
  isProductionEnvironment,
}) => {
  if (providerType === 'H5') {
    return new H5AdProvider({
      getAdsScriptStatus,
      isProductionEnvironment,
    });
  }

  if (providerType === 'GPT') {
    return new GptAdProvider();
  }

  if (providerType === 'VIGNETTE') {
    return new VignetteAdProvider();
  }

  return new NoopAdProvider();
};

export const __testables = {
  buildGptInterstitialAdUnitPath,
  getGptAdUnitPath,
};
